/**
 * ClaimBanner Component (Web)
 * 
 * Compact, dismissible claim promo banner for unclaimed restaurants.
 * Shows a simple message encouraging restaurant owners to claim their restaurant.
 * 
 * UX Constraints:
 * - Compact, low-contrast info banner
 * - Dismissible with localStorage (30-day TTL per restaurant)
 * - Only shows for unclaimed restaurants
 */

"use client";

import { useEffect, useState } from "react";
import { useLanguage } from "@/lib/useLanguage";
import { capturePostHogEvent } from "@/lib/posthog-client";
import { POSTHOG_EVENTS, POSTHOG_SOURCE } from "@/lib/posthog-events";

interface ClaimBannerProps {
  restaurantId: string;
  restaurantName?: string;
  isClaimed?: boolean;
}

const DISMISSAL_STORAGE_KEY_PREFIX = "menuhunter:claimBannerDismissed:";
const DISMISSAL_TTL_DAYS = 30;

export function ClaimBanner({ restaurantId, restaurantName, isClaimed: isClaimedProp }: ClaimBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const [checkingDismissal, setCheckingDismissal] = useState(true);
  const [isClaimed, setIsClaimed] = useState<boolean | undefined>(isClaimedProp);
  const { t } = useLanguage();

  // If isClaimed prop is provided, use it directly
  useEffect(() => {
    if (isClaimedProp !== undefined) {
      setIsClaimed(isClaimedProp);
    }
  }, [isClaimedProp]);

  // Check dismissal status on mount
  useEffect(() => {
    if (!restaurantId || typeof window === "undefined") {
      setCheckingDismissal(false);
      return;
    }

    try {
      const dismissalKey = `${DISMISSAL_STORAGE_KEY_PREFIX}${restaurantId}`;
      const dismissalData = localStorage.getItem(dismissalKey);

      if (dismissalData) {
        const { dismissedAt } = JSON.parse(dismissalData);
        const dismissedDate = new Date(dismissedAt);
        const daysSinceDismissal = Math.floor(
          (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        if (daysSinceDismissal < DISMISSAL_TTL_DAYS) {
          setDismissed(true);
        } else {
          // TTL expired, remove old dismissal
          localStorage.removeItem(dismissalKey);
        }
      }
    } catch (error) {
      console.warn("[ClaimBanner] Error checking dismissal:", error);
    } finally {
      setCheckingDismissal(false);
    }
  }, [restaurantId]);

  const handleDismiss = () => {
    if (typeof window === "undefined") return;
    
    try {
      const dismissalKey = `${DISMISSAL_STORAGE_KEY_PREFIX}${restaurantId}`;
      localStorage.setItem(
        dismissalKey,
        JSON.stringify({ dismissedAt: new Date().toISOString() })
      );
      setDismissed(true);
    } catch (error) {
      console.warn("[ClaimBanner] Error dismissing banner:", error);
    }
  };

  const handleClaim = () => {
    if (typeof window === "undefined") return;
    capturePostHogEvent(POSTHOG_EVENTS.RESTAURANT_CLAIM_STARTED, {
      source: POSTHOG_SOURCE,
      restaurant_id: restaurantId,
    });
    const url = `/download?restaurant_id=${encodeURIComponent(restaurantId)}`;
    window.location.href = url;
  };

  // Don't render if:
  // - Still checking dismissal
  // - Restaurant is claimed
  // - Banner was dismissed
  if (checkingDismissal || isClaimed === true || dismissed) {
    return null;
  }

  // If isClaimed is undefined and we don't have the prop, don't show (waiting for data)
  if (isClaimed === undefined && isClaimedProp === undefined) {
    return null;
  }

  const cb = t.restaurantSharePage?.claimBanner ?? {
    headline: "Are you the owner of this restaurant?",
    subtext: "",
    cta: "Claim in the app",
  };

  return (
    <div className="bg-white dark:bg-gray-900 border border-teal-200 dark:border-teal-700 rounded-lg p-3 mb-4 flex items-center gap-3 shadow-sm">
      <div className="flex-shrink-0">
        <svg
          className="w-5 h-5 text-teal-600 dark:text-teal-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 dark:text-gray-100 leading-tight">
          {cb.headline}
          {cb.subtext && <span className="text-xs text-gray-600 dark:text-gray-300"> · {cb.subtext}</span>}
        </p>
      </div>
      <button
        onClick={handleClaim}
        className="flex-shrink-0 px-3 py-1.5 text-xs font-semibold rounded-md bg-teal-600 text-white hover:bg-teal-700 transition-colors"
      >
        {cb.cta}
      </button>
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 p-1 text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100 transition-colors"
        aria-label="Dismiss"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
}
